import {
  ClientOptions,
  FeatureFlagsClient,
  FlagBaseContext,
  FlagOptions,
  FlagValue,
  Team,
} from '@getvim/feature-flags/client';
import { createFeatureFlagContext } from '@getvim/feature-flags/react';
import { isEmpty, trim } from 'lodash-es';
import { createLogger } from './logger';
// appFeatureFlagsContext is the lighter module (no @getvim/feature-flags import) on purpose, so
// this (heavier) module depends on it — not the other way around — keeping that import cheap for
// consumers that only need the resolved AppFeatureFlags shape.
import { AppFeatureFlags } from './appFeatureFlagsContext';

const logger = createLogger('featureFlags');

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface RUFlagContext extends FlagBaseContext {
  // Add additional context here if needed
}

const {
  rollout__key: rolloutKey,
  VIM_feature_flags__proxy_url: proxyUrl,
  VIM_feature_flags__unify_key: unifyKey,
} = globalThis.$vim_environment ?? {};

const ffClientOptions: ClientOptions<RUFlagContext> = {
  namespace: '',
  rolloutKey,
  proxyUrl: proxyUrl,
  unifyKey: isEmpty(trim(unifyKey)) ? undefined : unifyKey,
  defaultContext: {},
};

const { FeatureFlagProvider, useFeatureFlag } = createFeatureFlagContext<RUFlagContext>();

// Singleton client, also used directly (bypassing the React hook) by getAppFeatureFlags below, so
// every flag useEhrStateHandlers depends on can be resolved before the app tree ever mounts —
// none of them read via the hook at render time, so none can be observed mid-load.
const featureFlagsClient = FeatureFlagsClient.getInstance<RUFlagContext>(ffClientOptions);

// Mirrors useFeatureFlag's own error handling — a rejected getFlag() must not fail the whole
// fetch, just fall back to that one flag's default, same as the hook.
const getFlagOrDefault = async <V extends FlagValue>(
  options: FlagOptions<V, RUFlagContext>,
): Promise<V> => {
  try {
    return await featureFlagsClient.getFlag(options);
  } catch (error) {
    // Silent otherwise — an org running on this fallback looks identical to one where the flag
    // is genuinely off, so this is the only signal telling the two apart.
    logger.error('Failed to resolve feature flag, falling back to default', {
      flagName: options.flagName,
      defaultValue: options.defaultValue,
      error: error instanceof Error ? error.message : String(error),
      noPHI: true,
    });
    return options.defaultValue;
  }
};

// Keyed by AppFeatureFlags field name so getAppFeatureFlags can resolve all of them in one loop —
// a positional Promise.all + destructuring would type-check even if the two lists drifted out of
// order, silently swapping which value lands on which flag. defaultValue is per-entry (not shared)
// so an individual flag can default to true without touching the others.
const FLAG_DEFINITIONS: {
  key: keyof AppFeatureFlags;
  flagName: string;
  defaultValue: boolean;
  team?: Team;
}[] = [
  {
    key: 'shouldGetReferralModeByDateFF',
    flagName: 'referral_utility.shouldGetAthenaReferralModeByDate',
    defaultValue: false,
    team: Team.Interfaces,
  },
  {
    key: 'shouldCallOptumPatientEligibilityApiFF',
    flagName: 'referral_utility.shouldCallOptumPatientEligibilityApiFF',
    defaultValue: false,
    team: Team.Interfaces,
  },
  {
    key: 'shouldCachePatientEligibilityFF',
    flagName: 'referral_utility.shouldCachePatientEligibility',
    defaultValue: false,
    team: Team.Interfaces,
  },
  {
    key: 'shouldAutoUpdatePriorAuthStatusFF',
    flagName: 'referral_utility.shouldAutoUpdatePriorAuthStatus',
    defaultValue: false,
    team: Team.Interfaces,
  },
  // No `team` here, matching this flag's original useFeatureFlag call — omitting it changes
  // which underlying flag key gets resolved, so it can't be added without also migrating the
  // flag itself.
  {
    key: 'shouldUseLeadingReachProviderReferToName',
    flagName: 'referral_utility.shouldUseLeadingReachProviderReferToName',
    defaultValue: false,
  },
  {
    key: 'shouldUseCuroNotesText',
    flagName: 'referral_utility.shouldUseCuroNotesText',
    defaultValue: false,
    team: Team.Interfaces,
  },
  {
    key: 'shouldUseNewReferralIdFormat',
    flagName: 'referral_utility.shouldUseNewReferralIdFormat',
    defaultValue: false,
    team: Team.Interfaces,
  },
  {
    key: 'shouldUpdateSubmittedReferralMapOnResubmit',
    flagName: 'referral_utility.shouldUpdateSubmittedReferralMapOnResubmit',
    defaultValue: false,
    team: Team.Interfaces,
  },
];

/**
 * Resolves every feature flag useEhrStateHandlers depends on — up front, so AppWrapper can wait
 * for them alongside appConfig instead of the hook reading any of them via useFeatureFlag() at
 * render time, where a flag can still be mid-load (default value) when read.
 */
export const getAppFeatureFlags = async (flagContext: RUFlagContext): Promise<AppFeatureFlags> => {
  const resolvedEntries = await Promise.all(
    FLAG_DEFINITIONS.map(
      async ({ key, flagName, defaultValue, team }): Promise<[keyof AppFeatureFlags, boolean]> => [
        key,
        await getFlagOrDefault<boolean>({ flagName, defaultValue, team, flagContext }),
      ],
    ),
  );

  const result = {} as AppFeatureFlags;
  for (const [key, value] of resolvedEntries) {
    result[key] = value;
  }
  return result;
};

export { FeatureFlagProvider, ffClientOptions, useFeatureFlag };

export { Team } from '@getvim/feature-flags/client';
